import { forwardRef, type ChangeEvent, type InputHTMLAttributes, } from "react";
import styled from "styled-components";
import { getTheme, Stream, Div, Icon, Text,} from "@bracket/ui";
import type { IconName, ColorDeclaration, } from "@bracket/ui/types";



export declare type ToggleProps = {
	label?: string;
	checked?: boolean;
	disabled?: boolean;
	onChange?: (params: { checked: boolean; event: ChangeEvent<HTMLInputElement> }) => void;
	size?: "small" | "medium" | "large";
	iconOn?: IconName;
	iconOff?: IconName;
	onColor?: ColorDeclaration;
	offColor?: ColorDeclaration;
	iconOnColor?: ColorDeclaration;
	iconOffColor?: ColorDeclaration;
	thumbOnColor?: ColorDeclaration;
	thumbOffColor?: ColorDeclaration;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "size">;


/**
 * Toggle Component
 * 
 * A simple toggle switch component with customizable icons and colors.
 * 
 * @param {ToggleProps} props - The props for the toggle component
 * @param {boolean} props.checked - Whether the toggle is checked (on)
 * @param {boolean} props.disabled - Whether the toggle is disabled
 * @param {function} props.onChange - Callback function called when toggle state changes
 * @param {"small" | "medium" | "large"} props.size - Size variant of the toggle
 * @param {IconName} props.iconOn - Icon to show when toggle is ON (default: "Check")
 * @param {IconName} props.iconOff - Icon to show when toggle is OFF (default: "CrossStroke")
 * @param {ColorDeclaration} props.onColor - Track color when ON (default: "#1a7a4a")
 * @param {ColorDeclaration} props.offColor - Track color when OFF (default: "#3a3a3a")
 * @param {ColorDeclaration} props.iconOnColor - Icon color when ON (default: "#1a7a4a")
 * @param {ColorDeclaration} props.iconOffColor - Icon color when OFF (default: "#2a2a2a")
 * @returns {JSX.Element} The rendered toggle component
 */
export const Toggle = forwardRef<HTMLInputElement, ToggleProps>(({
	label,
	checked = false,
	disabled = false,
	onChange,
	size = "medium",
	iconOn = "Check",
	iconOnColor,
	iconOff = "CrossStroke",
	iconOffColor,
	onColor,
	offColor,
	thumbOnColor,
	thumbOffColor,
	className,
	...rest
}, ref) => {

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		if(disabled) return;
		onChange?.({
			checked: event.target.checked,
			event,
		});
	};

	return (
		<ToggleContainer className={className} $disabled={disabled}>
			{ label && <Text>{label}</Text> }
			<HiddenToggleInput ref={ref} type="checkbox" checked={checked} disabled={disabled} onChange={handleChange} {...rest} />
			<ToggleTrack $checked={checked} $disabled={disabled} $size={size} $color={checked ? onColor : offColor}>
					<ToggleThumb $checked={checked} $disabled={disabled} $size={size} $thumbColor={checked ? thumbOnColor : thumbOffColor}>
						<ToggleIconWrapper $size={size}>
							<Icon icon={checked ? iconOn : iconOff} color={checked ? iconOnColor : iconOffColor} scale={1} />
						</ToggleIconWrapper>
					</ToggleThumb>
			</ToggleTrack>
		</ToggleContainer>
	);
});


const ToggleContainer = styled.label<{ $disabled: boolean }>`
	display: flex;
	align-items: center;
	cursor: ${({ $disabled, }) => ($disabled ? "not-allowed" : "pointer")};
	user-select: none;
	flex-shrink: 0;
	gap: 1rem;
`;


const HiddenToggleInput = styled.input`
	position: absolute;
	opacity: 0;
	width: 0;
	height: 0;
	pointer-events: none;
`;


const ToggleTrack = styled(Div)<{ $checked: boolean; $disabled: boolean; $size: "small" | "medium" | "large"; $color?: ColorDeclaration; }>`
	position: relative;
	display: inline-flex;
	align-items: center;
	border-radius: 999px;
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	width: ${({ $size, }) => $size === "small" ? "36px" : $size === "large" ? "56px" : "44px"};
	height: ${({ $size, }) => $size === "small" ? "20px" : $size === "large" ? "32px" : "24px"};
	padding: ${({ $size, }) => $size === "small" ? "2px" : $size === "large" ? "3px" : "2px"};
	background-color: ${({ $checked, $color, }) => $color || $checked ? getTheme().outline : getTheme().surface_container_high};
	opacity: ${({ $disabled, }) => $disabled ? "0.6" : "1"};
`;


const ToggleThumb = styled(Stream)<{ $checked: boolean; $disabled: boolean; $size: "small" | "medium" | "large"; $thumbColor?: string; }>`
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
	width: ${({ $size, }) => $size === "small" ? "16px" : $size === "large" ? "26px" : "20px"};
	height: ${({ $size, }) => $size === "small" ? "16px" : $size === "large" ? "26px" : "20px"};
	color: ${({ $checked, }) => $checked ? getTheme().primary : getTheme().primary_on};
	background-color: ${({ $checked, $thumbColor, }) => $checked ? $thumbColor || getTheme().primary_on : getTheme().primary};
	transform: ${({ $checked, $size }) => $checked ? $size === "small" ? "translateX(16px)" : $size === "large" ? "translateX(24px)" : "translateX(20px)" : "translateX(0)"};
	opacity: ${({ $disabled, }) => $disabled ? "0.6" : "1"};
`;


const ToggleIconWrapper = styled(Stream)<{ $size: "small" | "medium" | "large"; }>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: ${({ $size, }) => $size === "small" ? "16px" : $size === "large" ? "26px" : "14px"};
	height: ${({ $size, }) => $size === "small" ? "16px" : $size === "large" ? "26px" : "14px"};
`;
